import React from 'react';
import { NodeData } from '../../types';
import { Copy, Video, Camera } from 'lucide-react';

interface TextPromptNodeProps {
  data: NodeData;
}

export const TextPromptNode: React.FC<TextPromptNodeProps> = ({ data }) => {
  const promptData = data.textPromptData || {
      sceneNumber: '?',
      visualDescription: 'No description available',
      cameraAngle: 'Wide'
  };
  // An excerpt dragged out of a script has no camera angle ("N/A"): then there is nothing to show
  const cameraAngle = promptData.cameraAngle && promptData.cameraAngle !== 'N/A' ? promptData.cameraAngle : '';
  // The card's header already reads "Scene 4" for a scene of a script: the number is not said again under it
  const saidInHeader = data.title === `Scene ${promptData.sceneNumber}`;

  const copyToClipboard = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(promptData.visualDescription);
      // Optional: Toast notification could go here
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {/* What the text is (the scene's number, the shot) and the button that copies it */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-cream-200 bg-cream-50">
          <div className="flex items-center gap-2 min-w-0">
              {!saidInHeader && (
                  <span className="ns-badge ns-badge-tone">
                      {promptData.sceneNumber}
                  </span>
              )}
              {cameraAngle && (
                  <span className="ns-t-meta flex items-center gap-1 min-w-0" title={`Camera: ${cameraAngle}`}>
                      <Camera size={12} className="shrink-0" /> <span className="truncate">{cameraAngle}</span>
                  </span>
              )}
          </div>
          <button
             onClick={copyToClipboard}
             className="ns-icon-btn ns-icon-btn-xs"
             title="Copy text"
             aria-label="Copy text"
          >
              <Copy />
          </button>
      </div>

      {/* Content: the scene as the script has it, so in the mono type like the script */}
      <div className="ns-t-mono flex-1 p-3 overflow-auto text-ink whitespace-pre-wrap break-words select-text">
          {promptData.visualDescription}
      </div>

      {/* Connection Hint */}
      <div className="ns-t-meta px-3 py-2 border-t border-cream-200 bg-cream-50 flex items-center justify-center gap-1">
          <Video size={12} /> Connect to an Image or Video node
      </div>
    </div>
  );
};
